/**
 * Shared arithmetic + vocabulary for the close wizards' count and review
 * screens (F-FE-N139). Pure functions only — the wizards keep the variance
 * maths that gates the closing note; these turn a delta into words and tone.
 */

/** Fallback in PKR. Mirrors backend dayops.DefaultVarianceThreshold. */
export const DEFAULT_VARIANCE_THRESHOLD = 100

/** Words for the two directions. Drawers are "over"/"short"; the marketplace app is "more/less in app". */
export interface TenderPhrasing {
  over: string
  short: string
}

export const DRAWER_PHRASING: TenderPhrasing = { over: 'over', short: 'short' }

export type TenderTone = 'ok' | 'warn' | 'bad'

/** Green when it reconciles, amber inside the threshold, rose past it. */
export function tenderTone(delta: number, threshold: number): TenderTone {
  const abs = Math.abs(delta)
  if (abs < 0.01) return 'ok'
  if (abs <= threshold) return 'warn'
  return 'bad'
}

/** "Rs 120 short" / "Rs 40 over" / "Rs 0" — direction stated, never a bare signed number. */
export function tenderDeltaLabel(
  delta: number,
  formatCurrency: (n: number) => string,
  phrasing: TenderPhrasing = DRAWER_PHRASING,
): string {
  if (Math.abs(delta) < 0.01) return formatCurrency(0)
  return `${formatCurrency(Math.abs(delta))} ${delta < 0 ? phrasing.short : phrasing.over}`
}

export function varianceToneClass(delta: number, threshold: number): string {
  return toneTextClass(tenderTone(delta, threshold))
}

export function toneTextClass(tone: TenderTone): string {
  switch (tone) {
    case 'ok':
      return 'text-emerald-700 dark:text-emerald-300'
    case 'warn':
      return 'text-amber-700 dark:text-amber-300'
    case 'bad':
      return 'text-rose-700 dark:text-rose-300'
  }
}

/** One line of a "where does this number come from?" breakdown. */
export interface BreakdownRow {
  label: string
  amount: number
  /** `muted` = shown for context but not part of the total (e.g. a marketplace receivable). */
  tone?: 'muted'
  /** Leading sign shown before the label on arithmetic rows ("+", "−"). */
  sign?: '+' | '−'
}

/** The breakdown behind an expected figure: rows, the total they make, and one plain sentence. */
export interface ExpectedBreakdownSpec {
  rows: BreakdownRow[]
  total: { label: string; amount: number }
  why?: string
}
